import {
  MAX_FRET,
  STANDARD_TUNING,
  allStrings,
  crossesGBShift,
  midiAt,
  openStringMidi,
  positionsOfPitchClass,
  midiToFretNote,
  type Position,
  type SearchOptions,
  type Tuning,
} from './fretboard';

/**
 * Intervals and octaves as fretboard shapes (topics C1, C3). Everything is
 * computed from pitch, so the G–B shift (+1 fret when a shape crosses the
 * major-3rd pair in standard tuning) falls out of the arithmetic rather than
 * being special-cased; `gbShift` flags where it applies so drills and worked
 * examples can teach it explicitly.
 */

export type IntervalName =
  'P1' | 'm2' | 'M2' | 'm3' | 'M3' | 'P4' | 'TT' | 'P5' | 'm6' | 'M6' | 'm7' | 'M7' | 'P8';

export interface IntervalInfo {
  readonly name: IntervalName;
  readonly semitones: number;
  /** Scale-degree label relative to a root, e.g. `b7`. */
  readonly degree: string;
  /** Generic interval number (1 = unison … 8 = octave); TT counts as a 4th/5th, i.e. 4. */
  readonly number: number;
  readonly long: string;
}

/** P1–P8 by semitone count (index = semitones). */
export const INTERVALS: readonly IntervalInfo[] = [
  { name: 'P1', semitones: 0, degree: '1', number: 1, long: 'perfect unison' },
  { name: 'm2', semitones: 1, degree: 'b2', number: 2, long: 'minor 2nd' },
  { name: 'M2', semitones: 2, degree: '2', number: 2, long: 'major 2nd' },
  { name: 'm3', semitones: 3, degree: 'b3', number: 3, long: 'minor 3rd' },
  { name: 'M3', semitones: 4, degree: '3', number: 3, long: 'major 3rd' },
  { name: 'P4', semitones: 5, degree: '4', number: 4, long: 'perfect 4th' },
  { name: 'TT', semitones: 6, degree: 'b5', number: 4, long: 'tritone' },
  { name: 'P5', semitones: 7, degree: '5', number: 5, long: 'perfect 5th' },
  { name: 'm6', semitones: 8, degree: 'b6', number: 6, long: 'minor 6th' },
  { name: 'M6', semitones: 9, degree: '6', number: 6, long: 'major 6th' },
  { name: 'm7', semitones: 10, degree: 'b7', number: 7, long: 'minor 7th' },
  { name: 'M7', semitones: 11, degree: '7', number: 7, long: 'major 7th' },
  { name: 'P8', semitones: 12, degree: '8', number: 8, long: 'perfect octave' },
];

/** The intervals taught as shapes (C3): m2 to P8. */
                                                                                              

                                                                      
                                                 
 

                                                                
                                                      
                                                                  
              
 

                                                               
                                      
 

/**
 * Octave-reduce a semitone distance to a simple interval: 0 is P1, any
 * non-zero multiple of 12 is P8, otherwise |semitones| mod 12.
 */
                                                                        
                                                                                               
                                  
                                                                 
                                                   
 

/** Signed pitch distance from `a` to `b` in semitones (positive when `b` is higher). */
                                 
              
              
                                   
           
                                               
 

/**
 * The interval between two dots, measured from the lower pitch to the higher
 * (order-independent) and octave-reduced, as a player names it: "name the
 * interval between two dots" (D5).
 */
                                
              
              
                                   
                 
                                                               
 

/**
 * The interval of `pos` as a degree above `root`, regardless of octave or
 * which is higher: G below a C root is still the P5 of C. Unisons are P1 and
 * other same-pitch-class positions P8.
 */
                                 
                 
                
                                   
                 
                                                   
                              
                                     
                                                                
 

export type IntervalDirection = 'up' | 'down';

/**
 * The position on `string` sounding exactly `interval` above (or below) the
 * root, or null if it falls off the neck. The G–B shift is handled because the
 * fret comes from pitch: a M3 from s4f5 (G) lands on s3f4, but from s3f5 (C)
 * it lands on s2f5, one fret higher than the fourths-tuning shape.
 */
                                    
                 
                         
                 
                                                                
                    
                                                
                                                  
                                                                           
                                                       
                                               
                          
 

/**
 * Every position in the search window that is `interval` above the root as a
 * degree (any octave, either side of the root), for "tap the b7" drills (D6)
 * that accept any correct position. P1 and P8 both mean the root's pitch
 * class; the root itself is excluded.
 */
                                     
                 
                         
                           
               
                                                
                                                                           
                                                                       
                                                               
    
 

export interface IntervalShape {
  readonly interval: IntervalName;
  /** String of the root (the lower note). */
  readonly fromString: number;
  /** String of the upper note. */
  readonly toString: number;
  /** Upper-note fret minus root fret. */
  readonly fretOffset: number;
  /**
   * True when the shape crosses the G–B pair, so it is one fret off the
   * all-fourths shape (higher going up the strings, lower coming down).
   */
  readonly gbShift: boolean;
}

/**
 * The movable shape of an ascending interval from a root on `fromString` to
 * the upper note on `toString` (same string allowed). Shapes are fret offsets,
 * so they hold anywhere the neck has room.
 */
                              
                         
                     
                   
                                   
                  
                                                                                     
          
             
               
             
                                                   
                                                          
    
 

/**
 * Every m2–P8 shape for every ordered string pair (including same-string
 * shapes), root string lowest-pitched first. Filter by `fretOffset` for
 * playable stretches; shapes to a lower-pitched string have large offsets.
 */
                               
                                                                      
                    
                                                
                                                      
                                               
                                  
                               
                               
                                                                                            
     
   
             
 

/**
 * The octave of `pos` on another string, nearest in fret to `pos` (C1 items
 * such as `octave:s6f5→s4`). Any non-zero number of octaves counts; on a tie
 * the higher octave wins. Null if no octave fits on that string.
 */
                               
                
                 
                                   
                    
                                   
                                              
                                   
                                                
                                    
                                                
                                                              
                                                                                       
                              
     
   
              
 
